import angular from "angular";

import { Arrays, CalculationRowType, Dates, EnumerationsLogic, IncomeTypesLogic, IrRow, PayStatsCalculationReportDataResult, PayStatsTypeDefinition, Reports, Translations } from "@salaxy/core";
import { UiHelpers } from "../../services";
import { CellComponent, RowComponent } from "tabulator-tables";

/**
 * Provides functionality for building ad hoc holiday pay reports.
 */
export class PayStatsReportQueryController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "Reports",
    "UiHelpers",
    "$routeParams",
    "$compile",
    "$scope"
  ];

  /** Raw report from api */
  public data: Array<{ type: PayStatsTypeDefinition, results: PayStatsCalculationReportDataResult[]}> = [];

  /** Current employment id */
  public employmentId: string;

  /** Flag for indicating query in progress. */
  public isQuerying: boolean = false;

  /** Report configuration settings. */
  public reportInclude: any;
  public reportOptions: any;
  public reportData: any;

  constructor(
    private reportsApi: Reports,
    private uiHelpers: UiHelpers,
    private $routeParams: angular.route.IRouteParamsService,
    private $compile: angular.ICompileService,
    private $scope: angular.IScope,
  ) {}

  /** Controller initialization */
  public $onInit() {
    const crudItemId = this.employmentId ?? (this.$routeParams.crudItemId ?? "").split("/")[0];
    this.employmentId = crudItemId;

    /** Add a css class for all rows related to the parent row. */
    function applyClassRecursive(row: RowComponent, className: string) {
      const el = row.getElement();
      el.classList.remove("nested-odd", "nested-even");
      el.classList.add(className);
      row.getTreeChildren()?.forEach((childRow: RowComponent) => applyClassRecursive(childRow, className));
    }

    this.reportOptions = {
      selectableRows: false,
      layout: "fitColumns",
      dataTree: true,
      dataTreeChildIndent: 30,
      dataTreeStartExpanded: [true, true, false],
      rowFormatter: (row: any) => {
        let depth = 0;
        let parent = row.getTreeParent();
        while (parent) {
          depth++;
          parent = parent.getTreeParent();
        }

        // Format the nested rows' odd/even classes starting from the second nested rows. (0th one is the main type, e.g. "KTA", so that will be ignored.)
        if (depth == 1) {
          const parent = row.getTreeParent()!;
          const siblings = parent.getTreeChildren();
          const idx = siblings.indexOf(row);
          const className = idx % 2 === 0 ? "nested-odd" : "nested-even";
          applyClassRecursive(row, className);
        }
      }
    };
  }

  /** Queries the report data. */
  public query() {
    this.isQuerying = true;
    this.data = [];
    this.reportsApi.getPayStatsCalculationReportData(this.employmentId)
      .then((result) => {
        // get all distinct types
        const types = Arrays.groupBy(result.results, x => x.type.id + "-" + x.type.kind);
        for(const idx in types) {
          this.data.push( {
            type: types[idx][0].type,
            results: types[idx],
          });
        }
        this.reportInclude = this.generateHeaders();
        this.reportData = this.generateReportData(this.data);
        this.isQuerying = false;
    });
  }

  /**
   * Generates column headers for the table.
   * @returns array of headers and their settings.
   */
  private generateHeaders() {
    return [
      { field: "type", title: Translations.get("SALAXY.REPORTS.payStatsReport.type"), width: 200, formatter: cell => cell.getValue() ? `<strong>${cell.getValue()}</strong>` : "", headerSort: false },
      { field: "timeframe", title: Translations.get("SALAXY.REPORTS.payStatsReport.timeframe"), width: 200, headerSort: false },
      { field: "result", title: Translations.get("SALAXY.REPORTS.payStatsReport.result"), width: 80, hozAlign: "right", headerHozAlign: "right" },
      { field: "calculation", title: Translations.get("SALAXY.REPORTS.payStatsReport.calculation"), formatter: "html" },
      { field: "info", title: Translations.get("SALAXY.REPORTS.payStatsReport.info"), headerSort: false },
      { field: "amount", title: Translations.get("SALAXY.REPORTS.payStatsReport.amount"), width: 150, hozAlign: "right", headerHozAlign: "right", headerSort: false },
      { field: "price", title: Translations.get("SALAXY.REPORTS.payStatsReport.price"), width: 150, hozAlign: "right", headerHozAlign: "right", formatter: "money", formatterParams: { symbol: "€", symbolAfter: true, decimal: ",", thousand: " ", }, headerSort: false },
      { field: "workingTime", title: Translations.get("SALAXY.REPORTS.payStatsReport.workingTime"), width: 150, hozAlign: "right", headerHozAlign: "right" },
      {
        field: "workingTimeType",
        title: Translations.get("SALAXY.REPORTS.payStatsReport.workingTimeType"),
        width: 200,
        formatter: (cell: CellComponent) => {
          return this.getBadgeFormatter(cell, "workingTimeType");
        },
        headerSort: false
      },
      { field: "salary", title: Translations.get("SALAXY.REPORTS.payStatsReport.salary"), width: 150, hozAlign: "right", headerHozAlign: "right", formatter: "money", formatterParams: { symbol: "€", symbolAfter: true, decimal: ",", thousand: " ", }},
      {
        field: "salaryType",
        title: Translations.get("SALAXY.REPORTS.payStatsReport.salaryType"),
        width: 200,
        formatter: (cell: CellComponent) => {
          return this.getBadgeFormatter(cell, "salaryType");
        },
        headerSort: false
      }
    ];
  }


  /**
   * Formats value with a badge component (InputPayStatsRowType.ts).
   * @param cell Cell data.
   * @param rowVal Row value type name for AngularJS component.
   * @returns DOM Node for Tabulator.
   */
  private getBadgeFormatter(cell: CellComponent, rowVal: string) {
    const el = angular.element(`<salaxy-input-pay-stats-row-type name='${rowVal}' label-type='no-label' ng-model='row.${rowVal}' read-only='true'></salaxy-input-pay-stats-row-type>`);
    const scope = (this.$scope.$new(true) as angular.IScope & { row: any });
    scope.row = cell.getData();

    this.$compile(el)(scope);
    scope.$applyAsync();

    return el[0];
  }

  /**
   * Generates report data for multiple nested rows.
   * @param data Report data.
   * @returns Report data in nested rows for Tabulator.
   */
  private generateReportData(data) {
    return data.map(payStatsCalc => ({
      // The main report row (eg. "KTA").
      type: this.getTypeLabel(payStatsCalc.type),
      // Each child is a timeframe of calculations.
      _children: payStatsCalc.results.map(result => ({
        timeframe: `${Dates.getFormattedDate(result.start)} - ${Dates.getFormattedDate(result.end)}`,
        result: result.result,
        workingTime: result.workingTime,
        salary: result.salary,
        // Calculation rows.
        _children: result.calculations.map(calc => ({
          calculation: `<a href="#/calc/details/${calc.calculationId}">${calc.calculationTitle} ${Dates.getFormattedDate(calc.salaryDate)}</a>`,
          info: `${Dates.getFormattedDate(calc.workStartDate)} - ${Dates.getFormattedDate(calc.workEndDate)}`,
          workingTime: calc.workingTime,
          salary: calc.salary,
          // Rows in each calculation.
          _children: calc.rows.map(row => ({
            info: this.getRowLabel(row.irRow),
            amount: row.irRow.count,
            price: row.irRow.price,
            workingTime: row.workingTime,
            workingTimeType: row.workingTimeType,
            salary: row.salary,
            salaryType: row.salaryType,
          })),
        })),
      })),
    }));
  }

  /** Returns label for the ir row. */
  public getRowLabel(irRow: IrRow): string {

    //TODO: add this logic to UiHelpers.

    if (irRow.message) {
      return irRow.message;
    }
    let rowType = irRow.data.calculationRowType || CalculationRowType.Unknown;
    rowType = (rowType.substr(0, 1).toLowerCase() + rowType.substr(1)) as CalculationRowType;
    const code = irRow.irData?.code as number;
    const kind = irRow.data.kind as string;

    let label = "";
    if (rowType === CalculationRowType.IrIncomeType) {
      label = IncomeTypesLogic.getAll(null).find( (x) => (kind && x.transactionCode === kind) || (code > 0 && x.code === code))?.label;
    } else if (rowType === CalculationRowType.MealBenefit && code !== 334) {
      // Meal benefit deductions
      label = IncomeTypesLogic.getAll(null).find( (x) => code > 0 && x.code === code)?.label;
    } else if([
      CalculationRowType.Unknown,
      CalculationRowType.CarBenefit,
      CalculationRowType.BicycleBenefit,
      CalculationRowType.SubsidisedCommute,
      CalculationRowType.TaxAtSource,
      CalculationRowType.HourlySalaryWithWorkingTimeCompensation
    ].includes(rowType))
    {
      label = IncomeTypesLogic.getAll(null).find( (x) => code > 0 && x.code === code)?.label;
    }
    else if([
      CalculationRowType.DailyAllowance,
      CalculationRowType.EmploymentTermination,
      CalculationRowType.WorkingTimeCompensation,
      CalculationRowType.Remuneration,
      CalculationRowType.OtherCompensation,
      CalculationRowType.NonProfitOrg,
      CalculationRowType.MealBenefit, // main MealBenefit
      CalculationRowType.Training,
      CalculationRowType.PaidSickLeave,
      CalculationRowType.TimeRatePay,
    ].includes(rowType) && kind) {
      label =  EnumerationsLogic.getEnumLabel(`${irRow.data.calculationRowType}Kind`, kind, null, null);
    }
    return label || EnumerationsLogic.getEnumLabel("CalculationRowType", rowType, null, null);

  }

  /** Returns label for type or result. */
  public getTypeLabel(type: PayStatsTypeDefinition): string {
    let label = type.description;
    if (!label)
    {
        label = type.kind + "/" + type.periodLength;
    }
    return label;
  }
}